import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import type { ChatReplyFolderResponse } from '@/core/generated/api';
import { chatReplyTemplateService } from '@/entities/ChatReplyTemplate/api/chatReplyTemplateService';
import { chatReplyFolderToRequest, chatReplyFolderToValues } from '@/entities/ChatReplyTemplate/mappers';
import { chatReplyFolderSchema } from '@/entities/ChatReplyTemplate/model/schema';
import type { ChatReplyFolderValues } from '@/entities/ChatReplyTemplate/model/schema';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import classes from './ChatReplyEditors.module.scss';

const { TextField, SelectField } = createRHFFields<ChatReplyFolderValues>('chat-reply-folder');

interface ChatReplyFolderEditorProps {
    folder?: ChatReplyFolderResponse;
    parentId?: string | null;
    folders: ChatReplyFolderResponse[];
    onSaved: (id: string) => void;
    onClose: () => void;
}

/** Редактирует папку шаблонов без перехода на другую страницу */
export const ChatReplyFolderEditor = (
    { folder, parentId, folders, onSaved, onClose }: ChatReplyFolderEditorProps
) => {
    const form = useUnsavedForm<ChatReplyFolderValues>({
        resolver: zodResolver(chatReplyFolderSchema),
        defaultValues: chatReplyFolderToValues(folder, parentId),
    });

    const canSave = useCanAccessPermission(folder?.id
        ? 'admin.chat-replies.edit'
        : 'admin.chat-replies.create');

    const busy = form.formState.isSubmitting;
    const unchanged = Boolean(folder?.id) && !form.formState.isDirty;
    const hasChildren = Boolean(folder?.id) && folders.some(item => item.parentId === folder?.id);
    const parents = folders.filter(item => !item.parentId && item.id !== folder?.id);

    /** Закрывает редактор после проверки изменений */
    const close = (): void => {
        if (busy) return;

        confirmUnsavedChanges(form.formState.isDirty, onClose);
    };

    /** Сохраняет папку шаблонов */
    const submit = async (values: ChatReplyFolderValues): Promise<void> => {
        if (!canSave || unchanged) return;

        try {
            const id = await chatReplyTemplateService.saveFolder(chatReplyFolderToRequest(values, folder?.version), folder?.id);

            form.reset(values);
            toast.success('Папка сохранена');
            onSaved(id);
        } catch (error: unknown) {
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось сохранить папку') });
        }
    };

    return <Modal
        visible
        onHide={close}
        closable={!busy}
        title={folder
            ? 'Изменить папку'
            : 'Новая папка'
        }
        size="md"
    >
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(submit)}
                className={classes.form}
                noValidate
            >
                <TextField
                    name="name"
                    label="Название папки"
                    maxLength={100}
                    isRequired
                    disabled={busy}
                />
                <SelectField
                    name="parentId"
                    label="Родительская папка"
                    disabled={busy || hasChildren}
                    options={[
                        {
                            id: null,
                            name: 'Корневая папка'
                        },
                        ...parents.map(item => ({
                            id: item.id ?? '',
                            name: item.name ?? ''
                        })),
                    ]}
                />
                {hasChildren &&
                    <p>Папку с подпапками можно оставить только на верхнем уровне.</p>
                }
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                }
                <div className={classes.actions}>
                    <ActionButton
                        type="button"
                        label="Отмена"
                        color="secondary"
                        disabled={busy}
                        onClick={close}
                    />
                    <ActionButton
                        type="submit"
                        label="Сохранить"
                        loading={busy}
                        disabled={!canSave || unchanged}
                    />
                </div>
            </form>
        </FormProvider>
    </Modal>;
};
